import { useCallback, useEffect } from 'react';
import { create } from 'zustand';

import { Schedule } from '@/features/calendar/types';

export type FilterType = {
  variant?: Schedule.Lesson['variant'];
  status?: Schedule.Lesson['status'];
};

export type FilterOptions = {
  force?: boolean;
};

type SchedulePageStore = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedMonth: string;
  setSelectedMonth: (selectedMonth: string) => void;
  filter: FilterType;
  setFilter: (filter: FilterType, options?: FilterOptions) => void;
  resetFilters: () => void;
};

export const useSchedulePageStore = create<SchedulePageStore>(set => ({
  isOpen: false,
  setIsOpen: (isOpen: boolean) => set({ isOpen }),
  selectedMonth: 'Select all',
  setSelectedMonth: (selectedMonth: string) => set({ selectedMonth }),
  filter: {},
  setFilter: (filter: FilterType, options?: FilterOptions) => {
    const { variant, status } = filter;
    if (variant) {
      set(prev => {
        if (prev.filter?.variant === variant && !options?.force) {
          return { ...prev, filter: {} };
        }
        return { ...prev, filter: { variant } };
      });
    } else if (status) {
      set(prev => {
        if (prev.filter?.status === status && !options?.force) {
          return { ...prev, filter: {} };
        }
        return { ...prev, filter: { status } };
      });
    }
  },
  resetFilters: () => set({ selectedMonth: 'Select all', filter: {} }),
}));

// Clears the Schedule drawer's open flag on mount. The drawer's `isOpen`
// is module-global, in-memory state that nothing resets on navigation, so
// a drawer left open on the dashboard would silently reopen on return.
// The standalone parent-connect pages (respond / book / edit-request /
// pc-ptm) live OUTSIDE the app shell and call this to drop that stale flag
// without touching the shared Navigation behaviour.
export const useCloseScheduleDrawerOnMount = () => {
  const setIsOpen = useSchedulePageStore(state => state.setIsOpen);
  useEffect(() => {
    setIsOpen(false);
  }, [setIsOpen]);
};

// Returns a callback that opens the Schedule drawer pre-filtered to the
// "Parent Connect / PTM" chip (variant `parent_connect`, forced on). The
// standalone parent-connect pages (book / respond / edit-request) call this
// on a successful action and then navigate to the dashboard: the app shell
// hosts the drawer (see navigation.tsx), so mounting it with `isOpen` already
// true lands the parent directly on their PC/PTM sessions. Mirrors the
// open-with-filter pattern in upcoming-session-card.tsx.
export const useOpenParentConnectSchedule = () => {
  const setIsOpen = useSchedulePageStore(state => state.setIsOpen);
  const setFilter = useSchedulePageStore(state => state.setFilter);
  return useCallback(() => {
    setFilter({ variant: 'parent_connect' }, { force: true });
    setIsOpen(true);
  }, [setFilter, setIsOpen]);
};
